// apps/console/src/services/api/sos-attention.ts
//
// The part of the emergency surface that works when nobody is looking at the tab.
//
// A modal only helps an operator whose eyes are already on the console. The alarms it announces are
// a worker falling, stopping moving, or pressing SOS — so the signal has to survive a minimised
// window, a different tab, and a room where somebody stepped away. That means a SIREN that repeats
// until it is answered, and an OS-level notification when the tab is hidden.
//
// A siren and not a beep (2026-10). Two soft rising notes every fifteen seconds read as a chat
// message, and operators asked for something that sounds like what it is. This is the sound of an
// emergency vehicle: a slow wail up and down, then a fast yelp, from two harsh voices, every few
// seconds until someone answers.
//
// Everything here is BEST-EFFORT and must never throw. Browsers refuse audio until the page has
// seen a user gesture, refuse notifications without permission, and jsdom has neither API — a
// console that crashed because it could not sound would be far worse than a silent one.

/** A new siren burst starts this often while an emergency is unanswered. */
const SIREN_CYCLE_MS = 4_000

/** How often the OS notification is raised again. Slower than the siren: a banner every 4 s is noise. */
const NOTIFY_REPEAT_MS = 15_000

const LOW_HZ = 650
const HIGH_HZ = 1_500

/** Peak level of the two voices together. Each is under 0.5, so the sum never clips. */
const SIREN_LEVEL = 0.32

/** Fast up-sweeps after the wail: each rises over most of its slot and drops back at its end. */
const YELP_COUNT = 8
const YELP_S = 0.17

/**
 * One burst, as (seconds from its start, frequency) points joined by straight ramps: a wail up and
 * back down, then the yelp. About three seconds, so a burst always ends before the next one starts.
 */
const SWEEP: ReadonlyArray<readonly [number, number]> = (() => {
  const points: Array<readonly [number, number]> = [
    [0, LOW_HZ],
    [0.85, HIGH_HZ],
    [1.7, LOW_HZ],
  ]

  for (let i = 0; i < YELP_COUNT; i += 1) {
    const at = 1.7 + i * YELP_S

    points.push([at + YELP_S - 0.02, HIGH_HZ], [at + YELP_S, LOW_HZ])
  }

  return points
})()

const BURST_S = SWEEP[SWEEP.length - 1]![0]

/**
 * The two voices: a sawtooth and a square a few hertz apart. Their harmonics make the sound cut
 * through a room and a laptop speaker, where a pure tone disappears, and the slight detune gives it
 * the beating of a real siren.
 */
const VOICES: ReadonlyArray<{ type: OscillatorType; ratio: number }> = [
  { type: 'sawtooth', ratio: 1 },
  { type: 'square', ratio: 1.006 },
]

type Ctor = new () => AudioContext

interface Burst {
  oscillators: OscillatorNode[]
  gain: GainNode
  /** In the context's clock. */
  endsAt: number
}

let audio: AudioContext | null = null
let sirenTimer: ReturnType<typeof setInterval> | null = null
let notifyTimer: ReturnType<typeof setInterval> | null = null
let notification: Notification | null = null
let bursts: Burst[] = []
let unlock: (() => void) | null = null

function audioContext(): AudioContext | null {
  if (audio) return audio

  // `webkitAudioContext` for older Safari. Absent entirely in jsdom, which is why this is a lookup
  // rather than a direct reference.
  const w = globalThis as unknown as { AudioContext?: Ctor; webkitAudioContext?: Ctor }
  const Ctx = w.AudioContext ?? w.webkitAudioContext

  if (!Ctx) return null

  try {
    audio = new Ctx()
  } catch {
    return null
  }

  return audio
}

/**
 * Start sound on the operator's next touch or key press, when the browser is holding it back.
 *
 * Autoplay policy keeps a page silent until it has seen a gesture. After a reload with nobody
 * touching the page, the first siren is refused; this is what lets it sound the moment anyone
 * reaches for the mouse, rather than never.
 */
function armUnlock(): void {
  if (unlock || typeof document === 'undefined') return

  const run = () => {
    disarmUnlock()
    void audio?.resume?.().catch(() => undefined)
  }

  unlock = run
  document.addEventListener('pointerdown', run, true)
  document.addEventListener('keydown', run, true)
}

function disarmUnlock(): void {
  if (!unlock || typeof document === 'undefined') return

  document.removeEventListener('pointerdown', unlock, true)
  document.removeEventListener('keydown', unlock, true)
  unlock = null
}

/**
 * Play one burst of the siren.
 *
 * Synthesised rather than loaded from a file: no asset to ship, cache or 404, and it plays the
 * instant it is needed instead of after a fetch. The whole sweep is scheduled at once on the audio
 * clock, so a busy main thread cannot make it stutter.
 */
function siren(): void {
  const ctx = audioContext()

  if (!ctx) return

  try {
    // Autoplay policy parks the context until a gesture. Resuming is a no-op when already running,
    // and a rejected promise here simply means the operator has not interacted yet.
    void ctx.resume?.()?.catch?.(() => undefined)
    if (ctx.state === 'suspended') armUnlock()

    // A held-back context has a stopped clock, so bursts scheduled on it would all start at the
    // same instant once it is let go — several sirens at once, clipping. Schedule another only once
    // the last has played out.
    bursts = bursts.filter(b => b.endsAt > ctx.currentTime)
    if (bursts.length > 0) return

    const start = ctx.currentTime + 0.02
    const end = start + BURST_S
    const gain = ctx.createGain()

    // Short ramps in and out instead of hard edges: an abrupt gate produces an audible click.
    gain.gain.setValueAtTime(0, start)
    gain.gain.linearRampToValueAtTime(SIREN_LEVEL, start + 0.04)
    gain.gain.setValueAtTime(SIREN_LEVEL, end - 0.06)
    gain.gain.linearRampToValueAtTime(0, end)
    gain.connect(ctx.destination)

    const oscillators = VOICES.map(({ type, ratio }) => {
      const osc = ctx.createOscillator()

      osc.type = type
      osc.frequency.setValueAtTime(SWEEP[0]![1] * ratio, start)
      for (const [at, hz] of SWEEP.slice(1)) osc.frequency.linearRampToValueAtTime(hz * ratio, start + at)
      osc.connect(gain)
      osc.start(start)
      osc.stop(end + 0.01)

      return osc
    })

    bursts.push({ oscillators, gain, endsAt: end + 0.01 })
  } catch {
    // A browser that refuses to play must not take the card down with it.
  }
}

/** Cut every burst now, the one sounding included: an answered emergency goes quiet at once. */
function silence(): void {
  const now = audio?.currentTime ?? 0

  for (const { oscillators, gain } of bursts) {
    try {
      // A 30 ms fade rather than a hard stop, for the same reason as the ramps above.
      gain.gain.cancelScheduledValues(now)
      gain.gain.setValueAtTime(gain.gain.value, now)
      gain.gain.linearRampToValueAtTime(0, now + 0.03)
      for (const osc of oscillators) osc.stop(now + 0.04)
    } catch {
      // Already stopped.
    }
  }

  bursts = []
}

/**
 * Raise an OS notification, but only when the tab is hidden.
 *
 * Visible tab means the modal is already on screen and a duplicate banner is just noise. Permission
 * is requested rather than assumed, and a refusal is final — we never ask twice.
 */
function notify(title: string, body: string): void {
  const N = (globalThis as unknown as { Notification?: typeof Notification }).Notification

  if (!N || typeof document === 'undefined' || !document.hidden) return

  const show = () => {
    try {
      notification?.close()
      // `requireInteraction` keeps it on screen until the operator deals with it, on the browsers
      // that honour it — the same reasoning as the repeating siren.
      notification = new N(title, { body, requireInteraction: true, tag: 'gisify-sos' })
    } catch {
      // Some browsers throw when constructing a Notification outside a service worker.
    }
  }

  if (N.permission === 'granted') return show()
  if (N.permission === 'denied') return

  void N.requestPermission?.()
    .then(p => {
      if (p === 'granted') show()
    })
    .catch(() => undefined)
}

/**
 * Start demanding attention, and keep demanding until {@link stopSosAttention}.
 *
 * Idempotent: calling it again for a second emergency restarts the cadence rather than stacking a
 * second one, so two alarms never sound over each other.
 */
export function startSosAttention(title: string, body: string): void {
  stopSosAttention()
  siren()
  notify(title, body)
  sirenTimer = setInterval(siren, SIREN_CYCLE_MS)
  notifyTimer = setInterval(() => notify(title, body), NOTIFY_REPEAT_MS)
}

/** Stop the siren and dismiss any notification. Safe to call when nothing is running. */
export function stopSosAttention(): void {
  if (sirenTimer) clearInterval(sirenTimer)
  if (notifyTimer) clearInterval(notifyTimer)
  sirenTimer = null
  notifyTimer = null
  silence()
  disarmUnlock()

  try {
    notification?.close()
  } catch {
    // Already gone.
  }

  notification = null
}

/** Test-only: drop the cached AudioContext so one spec cannot leak into the next. */
export function __resetSosAttentionForTests(): void {
  stopSosAttention()
  audio = null
}
